import { AppBar, Grid, Hidden, IconButton, Toolbar } from '@material-ui/core';
import { PaddedContent } from 'design-system';
import { useCallback, useMemo } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';

import {
  getExternalToken,
  getLoggedRole,
  isExternalLogin,
} from 'data-layer-cr/dist/authentication';

import {
  makeLogoutButtonMobile,
  makeUserLayoutMobile,
  mobileAssaLogo,
  mobileBell,
} from './mobileViewMethods';
import SearchToogleButtonMobile from './SearchToogleButtonMobile';

import {
  makeBackToAssanetDesktop,
  makeDesktopAssaLogo,
  makeLogoutButtonDesktop,
  makeNotificationButtonDesktop,
  makeProfileButtonDesktop,
  makeSearchDesktop,
  makeUserLayoutDesktop,
} from './desktopViewMethods';

import FilterPopper from './FilterPopper';
import makeDashboardToolbarStyles from './makeDashboardToolbarStyles';

function logOutAndRedirect(
  isBackToAssaNet: boolean,
  onLogoutClick: () => void,
  history: NavigateFunction,
): void {
  onLogoutClick();
  if (!isBackToAssaNet) {
    history(`/`);
    window.location.reload();
  }
}

function backToAssaNet(
  onLogoutClick: () => void,
  history: NavigateFunction,
): void {
  const externalToken = getExternalToken();
  logOutAndRedirect(true, onLogoutClick, history);
  const baseUrl = process.env.REACT_APP_ASSANET_URL;
  window.open(`${baseUrl}${externalToken}`, '_self');
}

type Props = {
  user: string;
  onSearchClick: () => void;
  searchString: string;
  onUpdateSearchString: (newString: string) => void;
  onLogoutClick: () => void;
  onFilterClick: () => void;
  showSearchInput: boolean;
  showFilterIcon: boolean;
  profileUnderstood: boolean;
  setProfileUnderstood: () => void;
};

const DashboardToolbar = ({
  user,
  onSearchClick,
  searchString,
  onUpdateSearchString,
  onLogoutClick,
  onFilterClick,
  showSearchInput,
  showFilterIcon,
  profileUnderstood,
  setProfileUnderstood,
}: Props): JSX.Element => {
  const classes = makeDashboardToolbarStyles();
  const history = useNavigate();
  const role = getLoggedRole();

  const externalLoginResult = isExternalLogin();
  const externalLogin = useMemo(
    () => externalLoginResult,
    [externalLoginResult],
  );

  const handleLogout = useCallback(
    (isBackToAssaNet: boolean): void => {
      logOutAndRedirect(isBackToAssaNet, onLogoutClick, history);
    },
    [onLogoutClick, history],
  );

  const handleBackToAssaNet = useCallback((): void => {
    backToAssaNet(onLogoutClick, history);
  }, [onLogoutClick, history]);

  const handleRedirectProfile = () => {
    if (role !== 'AGE') return;
    history('/profile');
  };

  return (
    <>
      <AppBar className={classes.appBar} position="fixed">
        <Toolbar className={classes.appBar}>
          {/* Mobile toolbar */}
          <Hidden mdUp>
            <Grid
              container
              spacing={1}
              alignItems="center"
              className={classes.appBarContent}
            >
              {mobileBell}
              {mobileAssaLogo}
              {makeLogoutButtonMobile(handleLogout, classes)}
              {makeUserLayoutMobile(user, classes)}
              <SearchToogleButtonMobile
                data-testid="search-element-mobile"
                onSearchClick={onSearchClick}
              />
            </Grid>
          </Hidden>

          {/* Desktop Toolbar */}
          <Hidden smDown>
            <PaddedContent defaultPadding={0} style={{ width: '100%' }}>
              <Grid container style={{ height: '100%' }}>
                <Grid
                  item
                  xs={7}
                  container
                  justifyContent="flex-start"
                  alignItems="center"
                  style={{ flexWrap: 'nowrap' }}
                >
                  {makeDesktopAssaLogo(classes)}
                  {showSearchInput &&
                    makeSearchDesktop(
                      searchString,
                      onUpdateSearchString,
                      classes,
                    )}
                  {showFilterIcon && (
                    <IconButton
                      id="sales-web-button-toolbarfilterbutton"
                      data-testid="filter-btn"
                      onClick={onFilterClick}
                    >
                      <FilterPopper />
                    </IconButton>
                  )}
                </Grid>
                <Grid
                  item
                  xs={5}
                  container
                  justifyContent="flex-end"
                  alignItems="center"
                  style={{ flexWrap: 'nowrap' }}
                >
                  {externalLogin &&
                    makeBackToAssanetDesktop(handleBackToAssaNet, classes)}
                  {makeUserLayoutDesktop(user, classes)}
                  {makeProfileButtonDesktop(
                    handleRedirectProfile,
                    classes,
                    profileUnderstood,
                    setProfileUnderstood,
                  )}
                  {makeNotificationButtonDesktop(classes)}
                  {makeLogoutButtonDesktop(handleLogout, classes)}
                </Grid>
              </Grid>
            </PaddedContent>
          </Hidden>
        </Toolbar>
      </AppBar>

      {/* Toolbar offset, to prevent elements from rendering behind it */}
      <div className={classes.appBarOffset} />
    </>
  );
};

export default DashboardToolbar;
